import { SupportedLocale, SUPPORTED_LOCALES, UI_TO_CMS_MAP } from '../../cms/cmsLocaleConstants';

export type QuotationMarks = {
    outerOpen: string;
    outerClose: string;
    innerOpen: string;
    innerClose: string;
};

const QUOTE_MARKS = {
    englishSmart: {
        outerOpen: '\u201C',
        outerClose: '\u201D',
        innerOpen: '\u2018',
        innerClose: '\u2019',
    },
    frenchGuillemets: {
        outerOpen: '\u00AB\u00A0',
        outerClose: '\u00A0\u00BB',
        innerOpen: '\u201C',
        innerClose: '\u201D',
    },
    angleGuillemets: {
        outerOpen: '\u00AB',
        outerClose: '\u00BB',
        innerOpen: '\u201C',
        innerClose: '\u201D',
    },
    germanLowHigh: {
        outerOpen: '\u201E',
        outerClose: '\u201C',
        innerOpen: '\u201A',
        innerClose: '\u2018',
    },
    polishLowHigh: {
        outerOpen: '\u201E',
        outerClose: '\u201D',
        innerOpen: '\u201A',
        innerClose: '\u2019',
    },
    japaneseKagi: {
        outerOpen: '\u300C',
        outerClose: '\u300D',
        innerOpen: '\u300E',
        innerClose: '\u300F',
    },
} as const;

type QuotationMarkPreset = (typeof QUOTE_MARKS)[keyof typeof QUOTE_MARKS];

const DEFAULT_QUOTATION_MARKS: QuotationMarks = QUOTE_MARKS.englishSmart;

const LOCALE_TO_QUOTATION_MARKS: Partial<Record<SupportedLocale, QuotationMarkPreset>> = {
    [UI_TO_CMS_MAP.es]: QUOTE_MARKS.angleGuillemets,
    [UI_TO_CMS_MAP.fr]: QUOTE_MARKS.frenchGuillemets,
    [UI_TO_CMS_MAP.de]: QUOTE_MARKS.germanLowHigh,
    [UI_TO_CMS_MAP.ru]: QUOTE_MARKS.angleGuillemets,
    [UI_TO_CMS_MAP.pl]: QUOTE_MARKS.polishLowHigh,
    [UI_TO_CMS_MAP.ja]: QUOTE_MARKS.japaneseKagi,
    [UI_TO_CMS_MAP.it]: QUOTE_MARKS.angleGuillemets,
};

const getNormalizedLocalePreference = (localePreference?: string): string =>
    localePreference?.trim().toLowerCase() || '';

export const getQuotationMarksForLocalePreference = (localePreference?: string): QuotationMarks => {
    const normalizedLocalePreference = getNormalizedLocalePreference(localePreference);
    if (!normalizedLocalePreference) return DEFAULT_QUOTATION_MARKS;

    const marksFromLocale = LOCALE_TO_QUOTATION_MARKS[normalizedLocalePreference as SupportedLocale];
    return marksFromLocale || DEFAULT_QUOTATION_MARKS;
};

export const wrapWithLocaleDoubleQuotes = (text: string, localePreference?: string): string => {
    const { outerOpen, outerClose } = getQuotationMarksForLocalePreference(localePreference);
    return `${outerOpen}${text}${outerClose}`;
};

export const getSupportedQuotationMarksByLocale = (): Record<SupportedLocale, QuotationMarks> => {
    const marksByLocale = {} as Record<SupportedLocale, QuotationMarks>;
    SUPPORTED_LOCALES.forEach((locale) => {
        marksByLocale[locale] = getQuotationMarksForLocalePreference(locale);
    });
    return marksByLocale;
};

/**
 * Leading / trailing whitespace that can pad a quotation mark, including the non-breaking
 * space used around French guillemets (e.g. `\u00AB\u00A0` / `\u00A0\u00BB`).
 */
const LEADING_QUOTE_PADDING_PATTERN = /^[\s\u00A0]+/;
const TRAILING_QUOTE_PADDING_PATTERN = /[\s\u00A0]+$/;

/**
 * Straight ASCII quotes, used as both the opening and closing mark in many languages.
 */
const STRAIGHT_QUOTE_CHARACTERS = ['"', "'"];

/**
 * Characters that can OPEN a quotation, independent of any single locale.
 *
 * We need to recognise quotes a user may have typed in any language, so this list is
 * deliberately broad. Note that several marks are used as both opening and closing
 * depending on the language, which is why some characters appear in both lists:
 * - Curly quotes (en), plus Finnish/Swedish which open with the "right/closing" glyph.
 * - Low-9 and high-reversed-9 marks (de, pl, cs, hu...).
 * - Angle quotes in both directions: French «…», but also German/Danish/Hungarian »…«,
 *   plus the single-angle equivalents ‹…› and ›…‹.
 * - CJK corner brackets, including white, halfwidth and vertical-presentation forms.
 * - Fullwidth ASCII quotes.
 */
const OPENING_QUOTE_CHARACTERS_LIST = [
    '\u2018', // ‘ left single quotation mark
    '\u2019', // ’ right single quotation mark (Swedish/Finnish open with this glyph)
    '\u201A', // ‚ single low-9 quotation mark (German/Czech inner open)
    '\u201B', // ‛ single high-reversed-9 quotation mark
    '\u201C', // “ left double quotation mark
    '\u201D', // ” right double quotation mark (Swedish/Finnish open with this glyph)
    '\u201E', // „ double low-9 quotation mark (German/Polish/Czech/Hungarian open)
    '\u201F', // ‟ double high-reversed-9 quotation mark
    '\u2039', // ‹ single left-pointing angle quotation mark
    '\u203A', // › single right-pointing angle quotation mark (German/Danish single open)
    '\u00AB', // « left-pointing double angle quotation mark (French open)
    '\u00BB', // » right-pointing double angle quotation mark (German/Danish/Swedish open)
    '\u300C', // 「 left corner bracket (Japanese/Chinese)
    '\u300E', // 『 left white corner bracket (Japanese/Chinese)
    '\uFE41', // ﹁ presentation form for vertical left corner bracket
    '\uFE43', // ﹃ presentation form for vertical left white corner bracket
    '\uFF62', // ｢ halfwidth left corner bracket
    '\uFF02', // ＂ fullwidth quotation mark
    '\uFF07', // ＇ fullwidth apostrophe
];

/**
 * Characters that can CLOSE a quotation, independent of any single locale. See
 * {@link OPENING_QUOTE_CHARACTERS_LIST} for why some marks appear in both lists.
 */
const CLOSING_QUOTE_CHARACTERS_LIST = [
    '\u2018', // ‘ left single quotation mark (German inner close)
    '\u2019', // ’ right single quotation mark
    '\u201C', // “ left double quotation mark (German outer close)
    '\u201D', // ” right double quotation mark
    '\u2039', // ‹ single left-pointing angle quotation mark (German/Danish single close)
    '\u203A', // › single right-pointing angle quotation mark (French single close)
    '\u00AB', // « left-pointing double angle quotation mark (German/Danish/Swedish close)
    '\u00BB', // » right-pointing double angle quotation mark (French close)
    '\u300D', // 」 right corner bracket (Japanese/Chinese)
    '\u300F', // 』 right white corner bracket (Japanese/Chinese)
    '\uFE42', // ﹂ presentation form for vertical right corner bracket
    '\uFE44', // ﹄ presentation form for vertical right white corner bracket
    '\uFF63', // ｣ halfwidth right corner bracket
    '\uFF02', // ＂ fullwidth quotation mark
    '\uFF07', // ＇ fullwidth apostrophe
];

/**
 * Single source of truth for opening-quote detection: the language-agnostic list above
 * plus straight ASCII quotes. This is a superset of every glyph the locale presets render
 * (guaranteed by a test), so it doubles as the rendering presets' detection coverage.
 */
export const OPENING_QUOTE_CHARACTERS: ReadonlySet<string> = new Set([
    ...STRAIGHT_QUOTE_CHARACTERS,
    ...OPENING_QUOTE_CHARACTERS_LIST,
]);

/**
 * Single source of truth for closing-quote detection. See {@link OPENING_QUOTE_CHARACTERS}.
 */
export const CLOSING_QUOTE_CHARACTERS: ReadonlySet<string> = new Set([
    ...STRAIGHT_QUOTE_CHARACTERS,
    ...CLOSING_QUOTE_CHARACTERS_LIST,
]);

/**
 * Returns true when the first non-whitespace character of `text` is a known opening quote.
 */
export const startsWithOpeningQuote = (text: string): boolean => {
    const trimmed = text.replace(LEADING_QUOTE_PADDING_PATTERN, '');
    const firstCharacter = trimmed[0];
    return OPENING_QUOTE_CHARACTERS.has(firstCharacter);
};

/**
 * Returns true when the last non-whitespace character of `text` is a known closing quote.
 */
export const endsWithClosingQuote = (text: string): boolean => {
    const trimmed = text.replace(TRAILING_QUOTE_PADDING_PATTERN, '');
    const lastCharacter = trimmed[trimmed.length - 1];
    return CLOSING_QUOTE_CHARACTERS.has(lastCharacter);
};
